/**
 * Column widths of a table in a note (v1.1.1, #24).
 *
 * Stored as an HTML comment directly before the table —
 * `<!-- praxi:table widths=20,50,30 -->`, whole percentages, one per column —
 * because Markdown has no place for them and a comment stays valid GFM that
 * every other Markdown tool ignores. The widths are part of the stored text,
 * so they go into the content hash, and a locked note's are fixed like the
 * rest of it.
 *
 * **Lenient on reading, strict on writing**, and one function does both:
 * `normalizeTableWidths` turns any acceptable list into the strict form —
 * whole numbers, at least 5 % each, exactly 100 together — and refuses the rest.
 * Because the strict form passes through it unchanged, a note that is merely
 * opened and saved again keeps its comment byte for byte, and the change
 * detection, which compares parsed trees, sees no change.
 *
 * - a wrong number of columns, a value ≤ 0 or one that is not a number: the
 *   comment is ignored, and the columns are equal;
 * - a sum other than 100: scaled proportionally;
 * - a column below 5 %: raised to 5 %, the difference taken proportionally
 *   from the others;
 * - more than 20 columns: no widths at all, since 5 % each cannot hold.
 *
 * A table without widths shows equal columns (`table-layout: fixed`), in the
 * editor and the reading pane alike. A table that was never resized carries no
 * comment.
 */
export const MIN_TABLE_COLUMN_PERCENT = 5;
const COMMENT = /^<!--[ \t]*praxi:table[ \t]+widths=([^\s]+)[ \t]*-->$/;
/** The widths a comment gives a table of `columns` columns, or null. */
export function parseTableWidthsComment(html, columns) {
    const match = COMMENT.exec(html.trim());
    if (!match?.[1])
        return null;
    const values = match[1]
        .split(',')
        .map((part) => (/^\d+(\.\d+)?$/.test(part) ? Number(part) : Number.NaN));
    return normalizeTableWidths(values, columns);
}
/** Whether an HTML string is a table-widths comment at all, valid or not. */
export function isTableWidthsComment(html) {
    return COMMENT.test(html.trim());
}
/** The comment for widths already in the strict form. */
export function tableWidthsComment(widths) {
    return `<!-- praxi:table widths=${widths.join(',')} -->`;
}
/**
 * Any acceptable list of widths in the strict form, or null for one that is
 * not acceptable. Idempotent: the strict form comes back unchanged.
 */
export function normalizeTableWidths(values, columns) {
    if (columns < 1 || values.length !== columns)
        return null;
    if (columns * MIN_TABLE_COLUMN_PERCENT > 100)
        return null;
    if (values.some((value) => !Number.isFinite(value) || value <= 0))
        return null;
    const sum = values.reduce((total, value) => total + value, 0);
    let shares = values.map((value) => (value / sum) * 100);
    // Raise the narrow ones to the minimum, taking the difference from the rest
    // in proportion; repeat, since taking may push another one below it.
    for (let round = 0; round < columns; round++) {
        const low = shares.map((share) => share < MIN_TABLE_COLUMN_PERCENT);
        if (!low.some(Boolean))
            break;
        const fixed = low.filter(Boolean).length * MIN_TABLE_COLUMN_PERCENT;
        const rest = shares.reduce((total, share, index) => (low[index] ? total : total + share), 0);
        shares = shares.map((share, index) => low[index] ? MIN_TABLE_COLUMN_PERCENT : (share / rest) * (100 - fixed));
    }
    // Whole numbers that add up to exactly 100: round down, then hand the
    // remainder to the largest fractions.
    const floors = shares.map((share) => Math.floor(share + 1e-9));
    let remainder = 100 - floors.reduce((total, value) => total + value, 0);
    const order = shares
        .map((share, index) => ({ index, fraction: share - (floors[index] ?? 0) }))
        .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
    for (const { index } of order) {
        if (remainder <= 0)
            break;
        floors[index] = (floors[index] ?? 0) + 1;
        remainder--;
    }
    return floors;
}
/** Widths after a column was inserted at `index`: the new one an equal share,
 *  the others scaled down in proportion. Null stays null — a table without
 *  widths keeps equal columns. */
export function widthsWithColumn(widths, index) {
    if (!widths)
        return null;
    const share = 100 / (widths.length + 1);
    const scaled = widths.map((width) => (width * (100 - share)) / 100);
    scaled.splice(index, 0, share);
    return normalizeTableWidths(scaled, scaled.length);
}
/** Widths after the columns from `from` up to, not including, `to` were
 *  deleted — one column by default: their share goes to the others in
 *  proportion. Null when one column or none would be left, since a single
 *  column needs no width. */
export function widthsWithoutColumn(widths, from, to = from + 1) {
    if (!widths)
        return null;
    const rest = widths.filter((_, column) => column < from || column >= to);
    if (rest.length < 2)
        return null;
    return normalizeTableWidths(rest, rest.length);
}
//# sourceMappingURL=note-table-widths.js.map